// =====================================================================
// src/screens/v2/RegisterBusiness.jsx
//
// V2.0 business registration — 3-step wizard.
// Reached via HomeStripV2's "+ Register" button when a non-owner taps it.
//
// Step 1 — The essentials:    name, category, tagline, city
// Step 2 — Contact & location: phone, locality, state, PIN, pickup/delivery, instructions, email
// Step 3 — Visuals & extras:   AGP partner ID, FSSAI (food only), T&C agreement, submit
//
// On submit: writes /businesses/{XXX-XXXX} with status=pending_verification.
// Admin manually verifies via Firestore Console. Once verified, the ✓ badge
// appears across the app (BusinessProfile, Manage hub, Connect List cards).
//
// Deferred (post-launch, tracked elsewhere):
//   - Phone OTP verification (admin handles during review for now)
//   - Multiple order alert numbers (only `phone` field for V2.0)
//   - Per-day business hours editor (free text only for now)
//   - Restaurant service modes (takeaway / dine-in / table booking)
//   - Delivery sub-options (who delivers, area, charges structure)
//   - Photo uploads (logo, cover, FSSAI certificate)
//   - Tags + category-aware suggestions
//   - Restricted-category warnings
//   - PIN-code → city/state auto-lookup
//   - Trial expiration enforcement (super-admin UI)
// =====================================================================

import React, { useState, useMemo, useRef } from 'react';
import { ArrowLeft, Check, Loader2, Camera, Image as ImageIcon, X, AlertTriangle, Plus } from 'lucide-react';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../lib/firebase';
import { generateUniqueBizCode } from '../../lib/v2/bizCode';
// Phase 2.3 C5 — registration-time photo uploads (logo, cover, FSSAI
// cert). All three upload AFTER setDoc creates the business doc
// (storage rule needs the doc to exist for the isBusinessOwner /
// fssaiVerified == false checks).
import {
  uploadBusinessLogo,
  uploadBusinessCover,
  uploadFssaiCert,
  validateImageFile,
} from '../../lib/v2/storagePhotos';
import './RegisterBusiness.css';

// ---- Category options ----
// Mirrors HomeConnectListV2/ManageBusinessTab + the full prototype grid.
// `food` flag flips the FSSAI section on in Step 3. Handmade is flagged
// food-eligible too since it commonly includes home-baked goods, pickles,
// jams etc that need FSSAI registration above turnover thresholds.
const CATEGORIES = [
  { id: 'food_beverages',  label: 'Food & Beverages',  emoji: '🍯', food: true },
  { id: 'dairy',           label: 'Dairy Products', emoji: '🥛', food: true },
  { id: 'bakery',          label: 'Bakery', emoji: '🍞', food: true },
  { id: 'groceries',       label: 'Groceries & Daily Needs', emoji: '🛒' },
  { id: 'apparel',         label: 'Fashion & Apparel', emoji: '👗' },
  { id: 'health_beauty',   label: 'Beauty & Personal Care', emoji: '💄' },
  { id: 'services',        label: 'Home Services', emoji: '🔧' },
  { id: 'wellness',        label: 'Health & Wellness', emoji: '🏥' },
  { id: 'education',       label: 'Education & Coaching', emoji: '📚' },
  { id: 'hobbies',         label: 'Hobbies, Arts & Crafts', emoji: '🎨' },
  { id: 'electronics',     label: 'Electronics & Repair', emoji: '📱' },
  { id: 'handmade',        label: 'Handmade & Custom', emoji: '🤲', food: true },
  { id: 'photography',     label: 'Photography & Events', emoji: '📷' },
  { id: 'other',           label: 'Other', emoji: '🏪' },
];

// Per-category example placeholder for "What do you offer?"
const TAGLINE_EXAMPLES = {
  food_beverages:  '"Cold-pressed juices and smoothies", "Home-baked chocolates and brownies", "North Indian tiffin service"',
  dairy:           '"Fresh cow milk delivered daily", "Homemade paneer and ghee", "Curd and lassi"',
  bakery:          '"Fresh bread baked daily", "Custom cakes for birthdays", "Cookies, muffins, and pastries"',
  groceries:       '"Daily vegetables and fruits", "Organic produce", "Door delivery within 3 km"',
  apparel:         '"Handpicked sarees, woven with love", "Streetwear and accessories", "Custom-tailored suits"',
  health_beauty:   '"Home salon services", "Natural skincare products", "Bridal makeup"',
  services:        '"24/7 plumbing repairs", "AC service and installation", "Electrical work"',
  wellness:        '"Yoga classes for all levels", "Physiotherapy at home", "Dental clinic"',
  education:       '"Maths coaching for grades 9-12", "Spoken English classes", "Music lessons"',
  hobbies:         '"Pottery classes", "Custom paintings", "Crochet and knitting workshops"',
  electronics:     '"Phone and laptop repairs", "Home appliance service", "Custom PC builds"',
  handmade:        '"Hand-poured candles", "Homemade pickles and jams", "Personalized gifts"',
  photography:     '"Wedding photography", "Event coverage", "Family portraits"',
  other:           'A short line describing what you do',
};

// Categories Aangan does not allow on the platform — shown in the
// restricted-category bottom sheet linked from the T&C checkbox copy.
const RESTRICTED_CATEGORIES = [
  'Alcohol / liquor',
  'Tobacco / cigarettes / gutkha',
  'Pharmacy / prescription medicines',
  'Raw meat without FSSAI licensing',
  'Adult content or services',
  'Gambling / betting / lottery',
  'Firearms / weapons',
  'Unregulated money lending / chit funds',
  'Crypto trading / investment advisory',
  'Hazardous chemicals',
  'Live animal trade',
  'Any business without applicable Indian licenses',
];

// Per-category tag suggestions surfaced under the tags input in Step 3.
// Up to 6 per category — keeps the tile row tidy. Free-text input still
// accepts anything; suggestions are just shortcuts.
const TAG_SUGGESTIONS = {
  food_beverages:  ['vegetarian', 'home-delivery', 'fresh', 'organic', 'jain', 'cake'],
  dairy:           ['fresh', 'organic', 'home-delivery', 'cow-milk', 'buffalo-milk', 'a2'],
  bakery:          ['eggless', 'fresh', 'custom-cakes', 'vegan', 'home-baked', 'gluten-free'],
  groceries:       ['organic', 'home-delivery', 'fresh', 'bulk-orders', 'daily-essentials'],
  apparel:         ['handpicked', 'custom-tailoring', 'wedding-wear', 'casual', 'ethnic'],
  health_beauty:   ['home-service', 'organic-products', 'bridal', 'unisex', 'ayurvedic'],
  services:        ['24-7', 'emergency', 'home-visit', 'guaranteed', 'experienced'],
  wellness:        ['certified', 'home-service', 'online-consultation', 'experienced'],
  education:       ['1-on-1', 'online', 'demo-class', 'home-tutor', 'group-classes'],
  hobbies:         ['classes', 'custom-orders', 'workshops', 'beginners-welcome'],
  electronics:     ['warranty', 'home-service', 'pickup-drop', 'genuine-parts'],
  handmade:        ['custom-orders', 'eco-friendly', 'wedding-gifts', 'personalized', 'homemade'],
  photography:     ['weddings', 'pre-wedding', 'events', 'portraits', 'candid'],
  other:           ['home-delivery', 'home-service', 'custom-orders'],
};

const MAX_TAGS = 10;

// Build the hours doc field from the form's hoursMode + related fields.
// Schema is forward-compatible with the future per-day editor:
//   { mode: '247' }                    — always open
//   { mode: 'appointment' }            — by-appointment-only
//   { mode: 'standard', open: 'HH:MM', close: 'HH:MM', days: ['mon',...] }
//   { mode: 'custom', schedule: { mon: {open,close}, ... } }   <-- future
function buildHoursObject(form) {
  const mode = form.hoursMode || 'standard';
  if (mode === '247') return { mode: '247' };
  if (mode === 'appointment') return { mode: 'appointment' };
  return {
    mode: 'standard',
    open: form.hoursOpen || '10:00',
    close: form.hoursClose || '21:00',
    days: Array.isArray(form.hoursDays) ? form.hoursDays : [],
  };
}

const DAY_LABELS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Initial step-1 state.
function initialState(currentUser) {
  return {
    // Step 1
    name: '',
    category: '',
    shortDescription: '',
    city: currentUser?.currentCity || '',
    // Step 2
    phone: '',
    addressLine1: '',
    addressLine2: '',
    state: '',
    pinCode: '',
    // Business hours (Step 6.0d.2 polish):
    // mode: '247' | 'appointment' | 'standard'. Standard hours apply
    // the same open/close to all days listed in hoursDays. Per-day
    // custom schedule editor is deferred to 6.0c.1 (post-launch).
    hoursMode: 'standard',
    hoursOpen: '10:00',
    hoursClose: '21:00',
    hoursDays: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'],   // Sun closed by default
    deliveryMode: 'pickup',   // 'pickup' | 'delivery' | 'both'
    deliveryInstructions: '',
    email: currentUser?.email || '',
    // Step 3
    description: '',          // 500-char "about your business" for the About tab
    tags: [],                 // array of strings, max MAX_TAGS
    instagramUrl: '',
    agpPartnerId: '',
    fssaiNumber: '',
    agreeToTerms: false,
  };
}

export default function RegisterBusiness({ currentUser, onBack, onSuccess }) {
  const [step, setStep] = useState(1);            // 1 | 2 | 3
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(null); // { bizId } once submitted

  const [form, setForm] = useState(() => initialState(currentUser));

  // Step 3: bottom sheet listing restricted categories — opened from the
  // T&C copy's "restricted category" link (was a useless terms.html link).
  const [showRestrictedSheet, setShowRestrictedSheet] = useState(false);

  // ---- Phase 2.3 C5: registration-time photo uploads ----
  // All files are HELD IN STATE until submit. They upload AFTER the
  // business doc is created in handleSubmit (storage rules require the
  // doc to exist for the isBusinessOwner cross-check). The uploads
  // themselves only write to Storage; a single consolidated updateDoc
  // at the end writes the URLs back to the business doc. Each upload
  // is independent — if one fails the others still proceed, and we
  // surface failures via a soft warning on the success screen.
  //
  // No crop step here (registration intentionally lightweight). The
  // EditBusinessOverlay (post-registration) still uses crop for precise
  // logo/cover framing. Visual rendering is fine either way because
  // the display containers use object-fit: cover.
  //
  // Per-business monthly-change counter (3/month for logo+cover) is
  // NOT bumped at registration — the first upload is "free" since the
  // business is being created. Future C2 edits start fresh at count=0
  // and bump normally.
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoError, setLogoError] = useState('');
  const logoFileRef = useRef(null);

  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [coverError, setCoverError] = useState('');
  const coverFileRef = useRef(null);

  const [fssaiCertFile, setFssaiCertFile] = useState(null);
  const [fssaiCertPreview, setFssaiCertPreview] = useState('');
  const [fssaiCertError, setFssaiCertError] = useState('');
  const fssaiCertFileRef = useRef(null);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  // ---- Tag handlers (Step 3) ----
  function addTag(raw) {
    const clean = (raw || '').trim().toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')   // sanitize to slug-style
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 30);
    if (!clean) return;
    if (form.tags.includes(clean)) return;
    if (form.tags.length >= MAX_TAGS) return;
    set('tags', [...form.tags, clean]);
  }

  function removeTag(t) {
    set('tags', form.tags.filter(x => x !== t));
  }

  // "Activate now" link in Step 3 — opens the user's email client with a
  // pre-filled subject line so info@aapsaathi.com can fast-track them.
  function handleActivateNow() {
    const subject = encodeURIComponent(
      `Activate Aangan Connect — ${form.name || 'New business'}`
    );
    const body = encodeURIComponent(
      `Hi Aangan team,\n\nI'd like to activate full access for my business.\n\nBusiness name: ${form.name || '(not yet entered)'}\nCity: ${form.city || '(not yet entered)'}\nCategory: ${form.category || '(not yet entered)'}\n\nThanks!`
    );
    window.location.href = `mailto:info@aapsaathi.com?subject=${subject}&body=${body}`;
  }

  const selectedCategory = useMemo(
    () => CATEGORIES.find(c => c.id === form.category) || null,
    [form.category]
  );

  // ---- Phase 2.3 C5: photo pick/remove handlers ----
  // Logo, cover, and FSSAI cert all share the same pick→preview pattern.
  // Three near-identical pairs of handlers (could be factored, but
  // explicit naming reads better at the call sites in Step3).

  function handleLogoPicked(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      validateImageFile(file);
    } catch (err) {
      setLogoError(err.message || 'Could not use that file');
      return;
    }
    setLogoError('');
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result);
    reader.onerror = () => setLogoError('Could not read that file.');
    reader.readAsDataURL(file);
  }
  function handleLogoRemove() {
    setLogoFile(null);
    setLogoPreview('');
    setLogoError('');
  }

  function handleCoverPicked(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      validateImageFile(file);
    } catch (err) {
      setCoverError(err.message || 'Could not use that file');
      return;
    }
    setCoverError('');
    setCoverFile(file);
    const reader = new FileReader();
    reader.onload = () => setCoverPreview(reader.result);
    reader.onerror = () => setCoverError('Could not read that file.');
    reader.readAsDataURL(file);
  }
  function handleCoverRemove() {
    setCoverFile(null);
    setCoverPreview('');
    setCoverError('');
  }

  function handleFssaiCertPicked(e) {
    const file = e.target.files?.[0];
    // Reset the input value so the same file can be re-picked after error.
    e.target.value = '';
    if (!file) return;
    // Strict jpeg/png/webp allowlist (same as everywhere else).
    try {
      validateImageFile(file);
    } catch (err) {
      setFssaiCertError(err.message || 'Could not use that file');
      return;
    }
    setFssaiCertError('');
    setFssaiCertFile(file);
    // Generate a data URL preview so the user sees what they picked.
    const reader = new FileReader();
    reader.onload = () => setFssaiCertPreview(reader.result);
    reader.onerror = () => setFssaiCertError('Could not read that file.');
    reader.readAsDataURL(file);
  }

  function handleFssaiCertRemove() {
    setFssaiCertFile(null);
    setFssaiCertPreview('');
    setFssaiCertError('');
  }

  // Validation per step. Returns null if OK, else an error message.
  function validateStep(s) {
    if (s === 1) {
      if (!form.name.trim()) return 'Business name is required';
      if (form.name.trim().length < 2) return 'Name is too short';
      if (!form.category) return 'Pick a category';
      if (!form.shortDescription.trim()) return 'Tell customers what you offer';
      if (!form.city.trim()) return 'City is required';
      return null;
    }
    if (s === 2) {
      if (!form.phone.trim()) return 'Phone number is required';
      // Indian phone: 10 digits, optionally with +91 prefix
      const phoneDigits = form.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) return 'Phone number should have 10 digits';
      if (phoneDigits.length > 13) return 'Phone number is too long';
      if (!form.addressLine1.trim()) return 'Address Line 1 is required';
      if (form.pinCode && !/^\d{6}$/.test(form.pinCode)) return 'PIN code should be 6 digits';
      return null;
    }
    if (s === 3) {
      if (!form.agreeToTerms) return 'Please confirm the terms to continue';
      if (form.fssaiNumber && !/^\d{14}$/.test(form.fssaiNumber)) {
        return 'FSSAI license should be 14 digits';
      }
      if (form.instagramUrl && !/^https?:\/\/(www\.)?instagram\.com\//i.test(form.instagramUrl.trim())) {
        return 'Instagram link should start with https://instagram.com/';
      }
      return null;
    }
    return null;
  }

  function handleNext() {
    const err = validateStep(step);
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError(null);
    setStep(step + 1);
    window.scrollTo(0, 0);
  }

  function handleBack() {
    if (step === 1) {
      onBack && onBack();
      return;
    }
    setSubmitError(null);
    setStep(step - 1);
    window.scrollTo(0, 0);
  }

  async function handleSubmit() {
    if (submitting) return;
    const err = validateStep(3);
    if (err) {
      setSubmitError(err);
      return;
    }
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setSubmitError('Please sign in to register a business');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    let bizId = null;
    let docData = null;
    try {
      // 1. Generate a unique XXX-XXXX share code (doc ID + slug).
      bizId = await generateUniqueBizCode();

      // 2. Build the doc. Field set matches what the firestore.rules
      //    create branch expects (status='pending_verification' etc).
      const phoneDigits = form.phone.replace(/\D/g, '');
      docData = {
        // Required by rules
        ownerId: uid,
        slug: bizId,
        name: form.name.trim(),
        category: form.category,
        city: form.city.trim(),
        status: 'pending_verification',
        verified: false,
        activated: false,
        fssaiVerified: false,
        connectCount: 0,
        orderCount: 0,
        // Editable owner fields (Step 1)
        shortDescription: form.shortDescription.trim(),
        // Step 2
        phone: phoneDigits.length === 10 ? `+91${phoneDigits}` : `+${phoneDigits}`,
        addressLine1: form.addressLine1.trim(),
        addressLine2: form.addressLine2.trim(),
        // `locality` is the field BusinessProfile/HomeStripV2 currently
        // read for "neighborhood" display. Mirror addressLine2 (or fall
        // back to addressLine1) so existing render code keeps working.
        locality: form.addressLine2.trim() || form.addressLine1.trim(),
        state: form.state.trim(),
        pinCode: form.pinCode.trim(),
        deliveryMode: form.deliveryMode,
        deliveryAvailable: form.deliveryMode !== 'pickup',
        deliveryInstructions: form.deliveryInstructions.trim(),
        email: form.email.trim(),
        // Step 3
        agpPartnerId: form.agpPartnerId.trim(),
        fssaiNumber: selectedCategory?.food ? form.fssaiNumber.trim() : '',
        description: form.description.trim(),
        tags: Array.isArray(form.tags) ? form.tags.slice(0, MAX_TAGS) : [],
        instagramUrl: form.instagramUrl.trim(),
        // Trial — admin sees this when reviewing
        trialDays: 15,
        trialStartedAt: serverTimestamp(),
        // Defaults so BusinessProfile renders consistently
        logo: selectedCategory?.emoji || '🏪',
        hours: buildHoursObject(form),
        // Audit
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // 3. Write (setDoc with explicit ID, not addDoc).
      await setDoc(doc(db, 'businesses', bizId), docData);

      // 3a. Phase 2.3 C5 — registration-time photo uploads. Logo, cover,
      // and FSSAI cert (food only) all upload AFTER setDoc above because
      // each storage rule needs the business doc to exist (for the
      // isBusinessOwner cross-check, plus fssaiVerified == false for the
      // cert path).
      //
      // Each upload is independent — if one fails the others still try.
      // We accumulate successful URLs into a single `patch` object and
      // do ONE updateDoc at the end, minimising Firestore writes.
      // Failed uploads are listed in uploadFailures[] and surfaced on
      // the success screen as a soft warning ("you can add them later
      // from Manage"). The business itself is created and usable
      // regardless of upload outcomes.
      const patch = {};
      const uploadFailures = [];

      if (logoFile) {
        try {
          patch.logoImageUrl = await uploadBusinessLogo(bizId, logoFile, uid);
        } catch (uploadErr) {
          console.warn('[RegisterBusiness] Logo upload failed:', uploadErr);
          uploadFailures.push('logo');
        }
      }
      if (coverFile) {
        try {
          patch.coverUrl = await uploadBusinessCover(bizId, coverFile, uid);
        } catch (uploadErr) {
          console.warn('[RegisterBusiness] Cover upload failed:', uploadErr);
          uploadFailures.push('cover');
        }
      }
      if (selectedCategory?.food && fssaiCertFile) {
        try {
          patch.fssaiCertUrl = await uploadFssaiCert(bizId, fssaiCertFile, uid);
        } catch (uploadErr) {
          console.warn('[RegisterBusiness] FSSAI cert upload failed:', uploadErr);
          uploadFailures.push('FSSAI cert');
        }
      }

      if (Object.keys(patch).length > 0) {
        patch.updatedAt = serverTimestamp();
        await updateDoc(doc(db, 'businesses', bizId), patch);
      }

      // 4. Show success screen with the share code.
      setSubmitted({ bizId, uploadFailures });
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('[RegisterBusiness] submit failed. code=', err?.code, 'message=', err?.message, 'full err:', err);
      // Build a debug-friendly error so the user can see what was sent
      // even without DevTools open. Required-by-rules fields are surfaced
      // explicitly so a mismatch is easy to spot.
      const debugSummary = docData ? {
        ownerId: docData.ownerId,
        slug: docData.slug,
        status: docData.status,
        verified: docData.verified,
        activated: docData.activated,
        fssaiVerified: docData.fssaiVerified,
        connectCount: docData.connectCount,
        orderCount: docData.orderCount,
        name: docData.name,
        category: docData.category,
        city: docData.city,
        'auth.currentUser.uid': uid,
      } : null;
      setSubmitError({
        code: err?.code || 'unknown',
        message: err?.message || 'Please try again.',
        bizId,
        debugSummary,
      });
    } finally {
      setSubmitting(false);
    }
  }

  // ============ SUCCESS SCREEN ============
  if (submitted) {
    return (
      <div className="v2rb-success">
        <div className="v2rb-success-emoji">🎉</div>
        <div className="v2rb-success-title">You're on Aangan!</div>
        <div className="v2rb-success-sub">
          Your business is live with a 15-day free trial. We'll review the details and add the verified ✓ badge within 24 hours.
        </div>
        {/* Phase 2.3 C5 — soft warning when one or more photo uploads
            failed after the business was already created. The business
            is fine; the merchant just needs to re-upload from their
            dashboard. We don't gate the success screen on this since
            all photo uploads are optional. */}
        {submitted.uploadFailures && submitted.uploadFailures.length > 0 && (
          <div
            style={{
              margin: '12px 0 0',
              padding: '10px 14px',
              borderRadius: 10,
              background: 'rgba(196, 90, 63, 0.08)',
              border: '1px solid rgba(196, 90, 63, 0.18)',
              fontSize: 13,
              lineHeight: 1.4,
              textAlign: 'left',
            }}
          >
            Your {submitted.uploadFailures.join(', ')} couldn't be uploaded right now. You can add {submitted.uploadFailures.length === 1 ? 'it' : 'them'} later from <b>Manage → Business info</b>. The rest of your business is saved.
          </div>
        )}
        <div className="v2rb-success-codecard">
          <div className="v2rb-success-codecard-label">YOUR BUSINESS CODE</div>
          <div className="v2rb-success-codecard-code">{submitted.bizId}</div>
          <div className="v2rb-success-codecard-hint">Share this with customers — they can connect by code.</div>
        </div>
        <button
          className="v2rb-success-primary"
          onClick={() => onSuccess && onSuccess(submitted.bizId)}
        >
          Open my business dashboard →
        </button>
      </div>
    );
  }

  // ============ HEADER ============
  const stepLabel =
    step === 1 ? 'The essentials' :
    step === 2 ? 'Contact & location' :
    'Visuals & extras';

  return (
    <div className="v2rb">
      <div className="v2rb-header">
        <button className="v2rb-back" onClick={handleBack} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <div className="v2rb-header-title">Register Business</div>
        <div style={{ width: 32 }} />
      </div>

      {/* Progress bar */}
      <div className="v2rb-progress">
        <div className={`v2rb-progress-seg ${step >= 1 ? 'on' : ''}`} />
        <div className={`v2rb-progress-seg ${step >= 2 ? 'on' : ''}`} />
        <div className={`v2rb-progress-seg ${step >= 3 ? 'on' : ''}`} />
      </div>

      <div className="v2rb-step-row">
        <span className="v2rb-step-name">{stepLabel.toUpperCase()}</span>
        <span className="v2rb-step-count">Step {step} of 3</span>
      </div>

      <div className="v2rb-body">
        {step === 1 && (
          <Step1 form={form} set={set} />
        )}
        {step === 2 && (
          <Step2 form={form} set={set} />
        )}
        {step === 3 && (
          <Step3
            form={form}
            set={set}
            isFood={!!selectedCategory?.food}
            categoryId={form.category}
            onAddTag={addTag}
            onRemoveTag={removeTag}
            onOpenRestricted={() => setShowRestrictedSheet(true)}
            onActivateNow={handleActivateNow}
            logoFile={logoFile}
            logoPreview={logoPreview}
            logoError={logoError}
            logoFileRef={logoFileRef}
            onLogoPicked={handleLogoPicked}
            onLogoRemove={handleLogoRemove}
            coverFile={coverFile}
            coverPreview={coverPreview}
            coverError={coverError}
            coverFileRef={coverFileRef}
            onCoverPicked={handleCoverPicked}
            onCoverRemove={handleCoverRemove}
            fssaiCertFile={fssaiCertFile}
            fssaiCertPreview={fssaiCertPreview}
            fssaiCertError={fssaiCertError}
            fssaiCertFileRef={fssaiCertFileRef}
            onFssaiCertPicked={handleFssaiCertPicked}
            onFssaiCertRemove={handleFssaiCertRemove}
          />
        )}

        {submitError && (
          typeof submitError === 'string' ? (
            <div className="v2rb-error">{submitError}</div>
          ) : (
            <div className="v2rb-error">
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Submit failed ({submitError.code})
              </div>
              <div style={{ fontSize: 12, marginBottom: 8 }}>{submitError.message}</div>
              {submitError.debugSummary && (
                <details style={{ fontSize: 11, marginTop: 6 }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                    Show diagnostic info
                  </summary>
                  <pre style={{
                    background: 'rgba(0,0,0,0.06)',
                    padding: 8,
                    borderRadius: 6,
                    marginTop: 6,
                    fontFamily: 'monospace',
                    fontSize: 10,
                    lineHeight: 1.6,
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                  }}>
{Array.isArray(submitError.debugSummary?.results)
  ? submitError.debugSummary.results.join('\n')
  : JSON.stringify(submitError.debugSummary, null, 2)}
                  </pre>
                  {submitError.bizId && (
                    <div style={{ fontSize: 11, marginTop: 4, color: '#7A4F3F' }}>
                      bizId attempted: <b>{submitError.bizId}</b>
                    </div>
                  )}
                </details>
              )}
            </div>
          )
        )}
      </div>

      <div className="v2rb-footer">
        {step > 1 && (
          <button className="v2rb-btn-ghost" onClick={handleBack} disabled={submitting}>
            Back
          </button>
        )}
        {step < 3 ? (
          <button className="v2rb-btn-primary" onClick={handleNext}>
            Continue
          </button>
        ) : (
          <>
            <button
              className="v2rb-btn-primary"
              onClick={handleSubmit}
              disabled={submitting || !form.agreeToTerms}
            >
              {submitting ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Loader2 size={14} className="v2rb-spin" /> Submitting…
                </span>
              ) : 'Submit registration'}
            </button>
          </>
        )}
      </div>

      {showRestrictedSheet && (
        <RestrictedSheet onClose={() => setShowRestrictedSheet(false)} />
      )}
    </div>
  );
}

// =====================================================================
// STEP 1 — Tell us about your business
// =====================================================================
function Step1({ form, set }) {
  return (
    <>
      <h1 className="v2rb-h1">Tell us about your business</h1>
      <p className="v2rb-sub">A few basics to get you listed in your local Aangan directory.</p>

      <div className="v2rb-field">
        <label className="v2rb-label">BUSINESS NAME</label>
        <input
          type="text"
          className="v2rb-input"
          placeholder="e.g. Sharma Sweets"
          value={form.name}
          onChange={e => set('name', e.target.value)}
          maxLength={60}
        />
        <div className="v2rb-counter">{form.name.length} / 60</div>
      </div>

      <div className="v2rb-field">
        <label className="v2rb-label">PICK A CATEGORY</label>
        <div className="v2rb-cat-grid">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              className={`v2rb-cat-tile ${form.category === cat.id ? 'sel' : ''}`}
              onClick={() => set('category', cat.id)}
              type="button"
            >
              <div className="v2rb-cat-emoji">{cat.emoji}</div>
              <div className="v2rb-cat-label">{cat.label}</div>
            </button>
          ))}
        </div>
        <div className="v2rb-hint">
          Some categories may need extra verification.
        </div>
      </div>

      <div className="v2rb-field">
        <label className="v2rb-label">WHAT DO YOU OFFER?</label>
        <input
          type="text"
          className="v2rb-input"
          placeholder="A short line about what you offer"
          value={form.shortDescription}
          onChange={e => set('shortDescription', e.target.value)}
          maxLength={80}
        />
        <div className="v2rb-counter">{form.shortDescription.length} / 80</div>
        {form.category && TAGLINE_EXAMPLES[form.category] && (
          <div className="v2rb-hint">
            <b>Examples:</b> {TAGLINE_EXAMPLES[form.category]}
          </div>
        )}
      </div>

      <div className="v2rb-field">
        <label className="v2rb-label">CITY</label>
        <input
          type="text"
          className="v2rb-input"
          placeholder="e.g. Mumbai"
          value={form.city}
          onChange={e => set('city', e.target.value)}
          maxLength={60}
        />
        <div className="v2rb-hint">Customers in this city will see you first.</div>
      </div>
    </>
  );
}

// =====================================================================
// STEP 2 — Contact & location
// =====================================================================
function Step2({ form, set }) {
  return (
    <>
      <h1 className="v2rb-h1">Where do orders go?</h1>
      <p className="v2rb-sub">Your contact and address. Admin will verify this before your ✓ badge.</p>

      <div className="v2rb-field">
        <label className="v2rb-label">PHONE NUMBER <span className="v2rb-required">(required)</span></label>
        <input
          type="tel"
          className="v2rb-input"
          placeholder="10-digit mobile number"
          value={form.phone}
          onChange={e => set('phone', e.target.value)}
          maxLength={15}
        />
        <div className="v2rb-hint">Order alerts go here via WhatsApp. Cannot be changed later.</div>
      </div>

      <div className="v2rb-field">
        <label className="v2rb-label">BUSINESS ADDRESS</label>
        <div className="v2rb-address-block">
          <input
            type="text"
            className="v2rb-input"
            placeholder="Address Line 1 · House / shop number, building"
            value={form.addressLine1}
            onChange={e => set('addressLine1', e.target.value)}
            maxLength={120}
          />
          <input
            type="text"
            className="v2rb-input"
            placeholder="Address Line 2 · Landmark, area (optional)"
            value={form.addressLine2}
            onChange={e => set('addressLine2', e.target.value)}
            maxLength={120}
          />
          <div className="v2rb-row">
            <input
              type="text"
              className="v2rb-input"
              placeholder="State"
              value={form.state}
              onChange={e => set('state', e.target.value)}
              maxLength={60}
              style={{ flex: 1 }}
            />
            <input
              type="tel"
              className="v2rb-input"
              placeholder="PIN code · 6 digits"
              value={form.pinCode}
              onChange={e => set('pinCode', e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              style={{ flex: 1 }}
            />
          </div>
        </div>
        <div className="v2rb-hint">City was entered in Step 1 — same field used for the directory filter.</div>
      </div>

      <div className="v2rb-field">
        <label className="v2rb-label">BUSINESS HOURS</label>
        <div className="v2rb-radio-group">
          <RadioCard
            checked={form.hoursMode === '247'}
            onClick={() => set('hoursMode', '247')}
            title="🌐 Open 24/7"
            sub="Customers can order anytime"
          />
          <RadioCard
            checked={form.hoursMode === 'appointment'}
            onClick={() => set('hoursMode', 'appointment')}
            title="📅 By appointment only"
            sub="Customer messages to schedule"
          />
          <RadioCard
            checked={form.hoursMode === 'standard'}
            onClick={() => set('hoursMode', 'standard')}
            title="🕒 Set standard hours"
            sub="Same times across selected days"
          />
        </div>

        {form.hoursMode === 'standard' && (
          <div className="v2rb-hours-detail">
            <div className="v2rb-hours-row">
              <input
                type="time"
                className="v2rb-input v2rb-time-input"
                value={form.hoursOpen}
                onChange={e => set('hoursOpen', e.target.value)}
              />
              <span className="v2rb-hours-sep">to</span>
              <input
                type="time"
                className="v2rb-input v2rb-time-input"
                value={form.hoursClose}
                onChange={e => set('hoursClose', e.target.value)}
              />
            </div>
            <div className="v2rb-hint" style={{ marginTop: 10, marginBottom: 6 }}>
              Open days (tap to toggle)
            </div>
            <div className="v2rb-hours-days">
              {DAY_LABELS.map(day => {
                const on = (form.hoursDays || []).includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    className={`v2rb-hours-day ${on ? 'on' : ''}`}
                    onClick={() => {
                      const cur = form.hoursDays || [];
                      const next = on ? cur.filter(d => d !== day) : [...cur, day];
                      set('hoursDays', next);
                    }}
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="v2rb-field">
        <label className="v2rb-label">HOW DO CUSTOMERS RECEIVE THEIR ORDERS?</label>
        <div className="v2rb-radio-group">
          <RadioCard
            checked={form.deliveryMode === 'pickup'}
            onClick={() => set('deliveryMode', 'pickup')}
            title="🛍️ Customer pickup only"
            sub="Customers come to your address to collect"
          />
          <RadioCard
            checked={form.deliveryMode === 'delivery'}
            onClick={() => set('deliveryMode', 'delivery')}
            title="🛵 We deliver"
            sub="Doorstep delivery to customers"
          />
          <RadioCard
            checked={form.deliveryMode === 'both'}
            onClick={() => set('deliveryMode', 'both')}
            title="🔁 Both available"
            sub="Customer can choose pickup or delivery"
          />
        </div>
      </div>

      <div className="v2rb-field">
        <label className="v2rb-label">INSTRUCTIONS FOR CUSTOMERS <span className="v2rb-optional">(optional)</span></label>
        <textarea
          className="v2rb-input"
          placeholder="e.g. Order 1 hour in advance. Free delivery within 3 km."
          value={form.deliveryInstructions}
          onChange={e => set('deliveryInstructions', e.target.value)}
          maxLength={200}
          rows={3}
          style={{ resize: 'vertical' }}
        />
        <div className="v2rb-counter">{form.deliveryInstructions.length} / 200</div>
      </div>

      <div className="v2rb-field">
        <label className="v2rb-label">EMAIL <span className="v2rb-optional">(optional)</span></label>
        <input
          type="email"
          className="v2rb-input"
          placeholder="your-business@email.com"
          value={form.email}
          onChange={e => set('email', e.target.value)}
          maxLength={120}
        />
      </div>
    </>
  );
}

// =====================================================================
// STEP 3 — Visuals & extras (Make it stand out)
// =====================================================================
function Step3({
  form, set, isFood, categoryId, onAddTag, onRemoveTag, onOpenRestricted, onActivateNow,
  // Phase 2.3 C5 — registration-time photo uploads. State lives in the
  // parent (alongside the submit handler that consumes the files);
  // these props expose them to the upload UI inside this step.
  logoFile, logoPreview, logoError, logoFileRef,
  onLogoPicked, onLogoRemove,
  coverFile, coverPreview, coverError, coverFileRef,
  onCoverPicked, onCoverRemove,
  fssaiCertFile, fssaiCertPreview, fssaiCertError, fssaiCertFileRef,
  onFssaiCertPicked, onFssaiCertRemove,
}) {
  const [tagInput, setTagInput] = useState('');
  const suggestions = TAG_SUGGESTIONS[categoryId] || TAG_SUGGESTIONS.other;
  const availableSuggestions = suggestions.filter(s => !form.tags.includes(s));

  function handleTagInputKey(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      onAddTag(tagInput);
      setTagInput('');
    }
  }
  function handleTagInputAdd() {
    onAddTag(tagInput);
    setTagInput('');
  }

  return (
    <>
      <h1 className="v2rb-h1">Make it stand out</h1>
      <p className="v2rb-sub">All optional — you can add these later from your profile.</p>

      {/* ---- LOGO & COVER (Phase 2.3 C5 — was a "coming soon" stub) ----
          Both are optional. Files are held in state until submit; upload
          happens after the business doc is created. No crop step here
          (registration is lightweight) — EditBusinessOverlay post-
          registration still does precise cropping. Visual rendering is
          fine either way thanks to object-fit: cover on the display
          containers. */}
      <div className="v2rb-field">
        <label className="v2rb-label">LOGO &amp; COVER <span className="v2rb-optional">(optional)</span></label>
        <div className="v2rb-photo-grid">
          {/* Logo tile — square, accepts a file or shows preview thumbnail */}
          <button
            type="button"
            className="v2rb-photo-card"
            onClick={() => !logoPreview && logoFileRef.current?.click()}
            style={{
              background: 'var(--surface, #FBF7EE)',
              border: logoPreview ? '1px solid var(--border, rgba(0,0,0,0.1))' : '1px dashed var(--border, rgba(0,0,0,0.15))',
              padding: 0,
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              minHeight: 110,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            {logoPreview ? (
              <>
                <img
                  src={logoPreview}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
                />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onLogoRemove(); }}
                  aria-label="Remove logo"
                  style={{
                    position: 'absolute', top: 4, right: 4, zIndex: 1,
                    width: 24, height: 24, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.5)', color: '#fff',
                    border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <X size={12} />
                </button>
              </>
            ) : (
              <>
                <Camera size={22} className="v2rb-photo-icon" />
                <span className="v2rb-photo-label">Logo</span>
              </>
            )}
          </button>

          {/* Cover tile — same pattern, 16:9-ish minHeight bumped via CSS */}
          <button
            type="button"
            className="v2rb-photo-card"
            onClick={() => !coverPreview && coverFileRef.current?.click()}
            style={{
              background: 'var(--surface, #FBF7EE)',
              border: coverPreview ? '1px solid var(--border, rgba(0,0,0,0.1))' : '1px dashed var(--border, rgba(0,0,0,0.15))',
              padding: 0,
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              minHeight: 110,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            {coverPreview ? (
              <>
                <img
                  src={coverPreview}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
                />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onCoverRemove(); }}
                  aria-label="Remove cover"
                  style={{
                    position: 'absolute', top: 4, right: 4, zIndex: 1,
                    width: 24, height: 24, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.5)', color: '#fff',
                    border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <X size={12} />
                </button>
              </>
            ) : (
              <>
                <ImageIcon size={22} className="v2rb-photo-icon" />
                <span className="v2rb-photo-label">Cover image</span>
              </>
            )}
          </button>
        </div>
        {/* Hidden file inputs — triggered by the buttons above */}
        <input
          ref={logoFileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={onLogoPicked}
        />
        <input
          ref={coverFileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={onCoverPicked}
        />
        {(logoError || coverError) && (
          <div className="v2rb-hint" style={{ color: '#B6671F', marginTop: 6 }}>
            {logoError || coverError}
          </div>
        )}
        <div className="v2rb-hint">Logo shows in directory; cover is your profile header.</div>
      </div>

      {/* ---- TAGS ---- */}
      <div className="v2rb-field">
        <label className="v2rb-label">TAGS <span className="v2rb-optional">(optional — helps customers find you)</span></label>

        <div className="v2rb-tag-input-row">
          <input
            type="text"
            className="v2rb-input"
            placeholder="Add a tag"
            value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            onKeyDown={handleTagInputKey}
            maxLength={30}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="v2rb-tag-add-btn"
            onClick={handleTagInputAdd}
            disabled={!tagInput.trim() || form.tags.length >= MAX_TAGS}
          >
            Add
          </button>
        </div>

        {form.tags.length > 0 && (
          <div className="v2rb-tag-chips">
            {form.tags.map(t => (
              <span key={t} className="v2rb-tag-chip">
                {t}
                <button
                  type="button"
                  className="v2rb-tag-chip-x"
                  onClick={() => onRemoveTag(t)}
                  aria-label={`Remove ${t}`}
                >
                  <X size={11} strokeWidth={2.5} />
                </button>
              </span>
            ))}
          </div>
        )}

        {availableSuggestions.length > 0 && (
          <>
            <div className="v2rb-hint" style={{ marginTop: 10, marginBottom: 6 }}>
              Popular for your category:
            </div>
            <div className="v2rb-tag-suggestions">
              {availableSuggestions.map(s => (
                <button
                  key={s}
                  type="button"
                  className="v2rb-tag-suggestion"
                  onClick={() => onAddTag(s)}
                  disabled={form.tags.length >= MAX_TAGS}
                >
                  <Plus size={11} strokeWidth={2.5} /> {s}
                </button>
              ))}
            </div>
          </>
        )}

        {form.tags.length >= MAX_TAGS && (
          <div className="v2rb-hint" style={{ marginTop: 8 }}>Max {MAX_TAGS} tags reached.</div>
        )}
      </div>

      {/* ---- ABOUT YOUR BUSINESS ---- */}
      <div className="v2rb-field">
        <label className="v2rb-label">ABOUT YOUR BUSINESS <span className="v2rb-optional">(optional)</span></label>
        <textarea
          className="v2rb-input"
          placeholder="A longer description shown on your profile"
          value={form.description}
          onChange={e => set('description', e.target.value)}
          maxLength={500}
          rows={4}
          style={{ resize: 'vertical' }}
        />
        <div className="v2rb-counter">{form.description.length} / 500</div>
      </div>

      {/* ---- SOCIAL LINKS ---- */}
      <div className="v2rb-field">
        <label className="v2rb-label">SOCIAL LINKS <span className="v2rb-optional">(optional)</span></label>
        <input
          type="url"
          className="v2rb-input"
          placeholder="https://instagram.com/your-handle"
          value={form.instagramUrl}
          onChange={e => set('instagramUrl', e.target.value)}
          maxLength={200}
        />
        <div className="v2rb-hint">Instagram only for now. More platforms coming soon.</div>
      </div>

      {/* ---- AGP PARTNER ID ---- */}
      <div className="v2rb-field">
        <label className="v2rb-label">AANGAN GROWTH PARTNER ID <span className="v2rb-optional">(optional)</span></label>
        <input
          type="text"
          className="v2rb-input"
          placeholder="e.g. AGP00001"
          value={form.agpPartnerId}
          onChange={e => set('agpPartnerId', e.target.value.toUpperCase())}
          maxLength={20}
        />
        <div className="v2rb-hint">If someone referred you to Aangan, enter their AGP ID here.</div>
      </div>

      {/* ---- COMPLIANCE (food only) ---- */}
      {isFood && (
        <div className="v2rb-compliance-card">
          <div className="v2rb-compliance-title">
            <Check size={14} strokeWidth={3} /> Compliance &amp; declaration
          </div>
          <div className="v2rb-field" style={{ marginTop: 12, marginBottom: 0 }}>
            <label className="v2rb-label">FSSAI LICENSE <span className="v2rb-optional">(food businesses)</span></label>
            <input
              type="tel"
              className="v2rb-input"
              placeholder="14-digit FSSAI license number"
              value={form.fssaiNumber}
              onChange={e => set('fssaiNumber', e.target.value.replace(/\D/g, '').slice(0, 14))}
              maxLength={14}
            />
            <div className="v2rb-hint">
              Required by Indian law for food businesses. Add now or later from your dashboard. Aangan won't show the verified ✓ badge until provided and approved.
            </div>
          </div>

          {/* Phase 2.3 C5 — Optional cert image. Helps the review team
              verify the number against the issued document. Once uploaded
              and admin flips fssaiVerified=true, the cert is locked from
              further owner writes (firestore.rules + storage.rules both
              enforce this — re-verification would require admin to flip
              fssaiVerified=false). */}
          <div className="v2rb-field" style={{ marginTop: 16, marginBottom: 0 }}>
            <label className="v2rb-label">FSSAI CERTIFICATE <span className="v2rb-optional">(optional, helps verification)</span></label>
            {fssaiCertPreview ? (
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  padding: 8,
                  border: '1px solid var(--border, rgba(0,0,0,0.1))',
                  borderRadius: 10,
                  background: 'var(--surface, #FBF7EE)',
                }}
              >
                <img
                  src={fssaiCertPreview}
                  alt=""
                  style={{
                    width: 64,
                    height: 64,
                    objectFit: 'cover',
                    borderRadius: 6,
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                  <div style={{ fontWeight: 500 }}>{fssaiCertFile?.name || 'Cert image'}</div>
                  <div style={{ color: 'var(--ink-soft, #6E6F84)', fontSize: 11, marginTop: 2 }}>
                    Will upload after you submit
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onFssaiCertRemove}
                  aria-label="Remove cert"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 6,
                    color: 'var(--ink-soft, #6E6F84)',
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="v2rb-input"
                onClick={() => fssaiCertFileRef.current?.click()}
                style={{
                  textAlign: 'left',
                  background: 'var(--surface, #FBF7EE)',
                  border: '1px dashed var(--border, rgba(0,0,0,0.15))',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: 'var(--ink-soft, #6E6F84)',
                }}
              >
                <Camera size={16} />
                <span>Upload a photo of your FSSAI certificate</span>
              </button>
            )}
            <input
              ref={fssaiCertFileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={onFssaiCertPicked}
            />
            {fssaiCertError && (
              <div className="v2rb-hint" style={{ color: '#B6671F', marginTop: 6 }}>
                {fssaiCertError}
              </div>
            )}
            <div className="v2rb-hint">
              JPEG, PNG, or WebP. Visible only to you and the Aangan review team.
            </div>
          </div>
        </div>
      )}

      {/* ---- T&C ---- */}
      <div className="v2rb-field">
        <label className="v2rb-checkbox-row">
          <input
            type="checkbox"
            checked={form.agreeToTerms}
            onChange={e => set('agreeToTerms', e.target.checked)}
          />
          <span>
            I confirm my business holds all required licenses as per Indian law, is not in any{' '}
            <button
              type="button"
              className="v2rb-inline-link"
              onClick={(e) => { e.preventDefault(); onOpenRestricted(); }}
            >
              restricted category
            </button>,
            and I agree to Aangan's{' '}
            <a href="https://aangan.aapsaathi.com/terms.html" target="_blank" rel="noopener">Terms of Service</a> and{' '}
            <a href="https://aangan.aapsaathi.com/privacy.html" target="_blank" rel="noopener">Privacy Policy</a>.
          </span>
        </label>
      </div>

      {/* ---- TRIAL + ACTIVATE NOW ---- */}
      <div className="v2rb-trial-card">
        <span className="v2rb-trial-emoji">🎁</span>
        <div>
          <b>15 days free</b> when you're approved. To continue after that, you can{' '}
          <a href="mailto:info@aapsaathi.com">email info@aapsaathi.com</a>.
        </div>
      </div>

      <button
        type="button"
        className="v2rb-activate-link"
        onClick={onActivateNow}
      >
        Already know you want full access? Activate now →
      </button>
    </>
  );
}

// =====================================================================
// Restricted-categories bottom sheet — opened from the T&C copy
// =====================================================================
function RestrictedSheet({ onClose }) {
  return (
    <div className="v2rb-sheet-backdrop" onClick={onClose}>
      <div className="v2rb-sheet" onClick={e => e.stopPropagation()}>
        <div className="v2rb-sheet-handle" />
        <div className="v2rb-sheet-header">
          <div className="v2rb-sheet-title">
            <AlertTriangle size={16} strokeWidth={2.5} style={{ color: '#B8893A' }} />
            Restricted on Aangan
          </div>
          <button className="v2rb-sheet-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="v2rb-sheet-body">
          <p className="v2rb-sheet-intro">
            Aangan does not allow businesses in these categories. If your business doesn't fit, please contact{' '}
            <a href="mailto:aangansupport@gmail.com">aangansupport@gmail.com</a>.
          </p>
          <div className="v2rb-sheet-list">
            {RESTRICTED_CATEGORIES.map(cat => (
              <div key={cat} className="v2rb-sheet-row">
                <span className="v2rb-sheet-row-icon">🚫</span>
                <span>{cat}</span>
              </div>
            ))}
          </div>
          <p className="v2rb-sheet-footnote">
            By registering, you confirm your business is not in any restricted category and complies with Indian law.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---- Small subcomponent: pickup/delivery radio card ----
function RadioCard({ checked, onClick, title, sub }) {
  return (
    <button
      type="button"
      className={`v2rb-radio ${checked ? 'sel' : ''}`}
      onClick={onClick}
    >
      <span className={`v2rb-radio-dot ${checked ? 'sel' : ''}`}>
        {checked && <span className="v2rb-radio-dot-inner" />}
      </span>
      <span className="v2rb-radio-text">
        <span className="v2rb-radio-title">{title}</span>
        <span className="v2rb-radio-sub">{sub}</span>
      </span>
    </button>
  );
}
